/**
 * Link reports (owner, 2026-10-02): "if a user clicks something that dies ... notifies us like a question to update that link".
 * The pure half. The server (services/api/src/linkreports/) re-checks the URL, stores one item per URL and tells the team.
 *
 * | Rule | Why |
 * |---|---|
 * | L1 One item per URL: the key is the URL with scheme case, host case, "www.", default port, fragment, a trailing slash and tracking parameters removed. Repeat reports add a count and the latest note | The team fixes a link once, however many people hit it |
 * | L2 Only public http(s) addresses: no other scheme, no credentials, no host that is a bare name, "localhost" or a private/loopback/link-local number | A report must never make our server call something inside our network (SSRF) |
 * | L3 A report carries: the URL, the page (path only), the link key if the page knows one, state and county, a note of one line, the kind of link. Never a name, email or phone number: the note is scrubbed like the questions queue | The questions queue keeps the user id of a signed-in asker and nothing else; so does this |
 * | L4 Report and contact links are safety-critical: the team hears at once. Everything else is batched into a daily digest unless it has been reported LINK_URGENT_COUNT times | An inspector's phone number or the "report an illness" page that is dead is a food-safety problem today |
 * | L5 The re-check decides the label: confirmed broken (4xx/5xx, no such host, timeout), works for us (2xx), redirected (2xx at another address, which we suggest) | "Works for us" can still be a regional or temporary problem, so the item stays open for a person to judge |
 */
import type { IsoDateTime } from "./model.ts";

/** "official-data": a diner says a health department's result on /find/ looks wrong ("Data to fix"; docs/OFFICIAL-RESULTS.md). */
export type ReportLinkKind = "agency" | "contact" | "training" | "report" | "source" | "other" | "official-data";
export const REPORT_LINK_KINDS: readonly ReportLinkKind[] = ["agency", "contact", "training", "report", "source", "other", "official-data"];
export const isReportLinkKind = (k: unknown): k is ReportLinkKind => typeof k === "string" && (REPORT_LINK_KINDS as readonly string[]).includes(k);

/** L4: the kinds whose death is a safety problem today. */
export const SAFETY_CRITICAL_KINDS: readonly ReportLinkKind[] = ["report", "contact"];
export const isSafetyCriticalKind = (k: ReportLinkKind): boolean => (SAFETY_CRITICAL_KINDS as readonly string[]).includes(k);
/** L4: this many reports of one URL is "many people hit it": tell the team at once. */
export const LINK_URGENT_COUNT = 3;

export type LinkCheckVerdict = "broken" | "works" | "redirected";
export interface LinkCheck {
  verdict: LinkCheckVerdict;
  /** The last HTTP status we saw (absent for a DNS failure or a timeout). */
  status?: number;
  /** Where the request ended (differs from the URL when it was redirected). */
  finalUrl?: string;
  checkedAt: IsoDateTime;
  /** Why it is broken, when no status says so: "no such host", "timed out", "refused". */
  error?: string;
  /** What to try instead (verdict redirected only). */
  suggestedUrl?: string;
  /** A line for the team: "This site often refuses automated checks", "Lands on their home page". */
  note?: string;
}
export const LINK_VERDICT_LABEL: Record<LinkCheckVerdict, string> = { broken: "Confirmed broken", works: "Works for us", redirected: "Redirected" };

export type LinkReportStatus = "open" | "fixed" | "not_broken";

/**
 * "Ask for correction" on an official result (owner, 2026-10-05): what a person says looks wrong, for which inspection, an
 * optional note and an optional email for a reply. Kept on the place's one "Data to fix" item (the newest 20); the email is
 * cleared when the item is resolved.
 */
export const CORRECTION_ISSUES = ["score", "date", "violation", "not_ours", "status", "other"] as const;
export type CorrectionIssue = (typeof CORRECTION_ISSUES)[number];
export const CORRECTION_ISSUE_LABEL: Record<CorrectionIssue, string> = {
  score: "The score or result is wrong", date: "The date is wrong", violation: "A violation is listed incorrectly",
  not_ours: "This is not our business", status: "Closed or reopened status is wrong", other: "Something else",
};
export const isCorrectionIssue = (v: unknown): v is CorrectionIssue => typeof v === "string" && (CORRECTION_ISSUES as readonly string[]).includes(v);
export interface OfficialCorrection { at: IsoDateTime; issue: CorrectionIssue; /** YYYY-MM-DD of the inspection it is about, or null (the place itself). */ inspectionDate: string | null; note: string | null; /** For a reply; cleared once resolved. */ contact: string | null }
export const MAX_CORRECTIONS = 20;

export interface LinkReportView {
  id: string;
  url: string;
  kind: ReportLinkKind;
  /** Where it appears: every page path that sent a report. */
  pages: string[];
  /** Every data key that sent a report (e.g. "nv/snhd/permit.renew"). */
  keys: string[];
  count: number;
  firstReportedAt: IsoDateTime;
  lastReportedAt: IsoDateTime;
  lastNote: string | null;
  states: Record<string, number>;
  counties: string[];
  check: LinkCheck | null;
  status: LinkReportStatus;
  /** What the team typed for "Update link", or what the data source took. */
  replacementUrl: string | null;
  resolvedAt: IsoDateTime | null;
  /** True when the team's "Update link" can change the URL in place (a data source owns the key). */
  editable: boolean;
  /** Where to change it by hand when it is not editable: the data key, else the pages. */
  whereToFix: string;
  safetyCritical: boolean;
  /** Queued for the research routine ("find the replacement"). */
  researchQueued: boolean;
  /** "Ask for correction" details (official-data only), newest first. */
  corrections?: OfficialCorrection[];
}

/** `data-link-key` values: lowercase path-like keys, e.g. "nv/snhd/permit.renew". */
export const LINK_KEY = /^[a-z0-9][a-z0-9_.:/-]{0,119}$/;
export const cleanLinkKey = (k: unknown): string | undefined => (typeof k === "string" && LINK_KEY.test(k.trim().toLowerCase()) ? k.trim().toLowerCase() : undefined);

/** A page or screen name: a path ("/learn/rules") or a short screen name ("Learn"); never a query string. */
export function cleanLinkPage(v: unknown): string {
  const s = typeof v === "string" ? v : "";
  const p = s.split(/[?#]/)[0]!.trim();
  return p.length > 0 && p.length <= 200 && /^[A-Za-z0-9\-_./%: ]+$/.test(p) ? p : "";
}

/** Parameters that identify a visit, not a page. */
const TRACKING = /^(utm_[a-z]+|fbclid|gclid|msclkid|mc_cid|mc_eid|ref|ref_src|_hsenc|_hsmi|igshid)$/i;
/** Parameters that may carry a secret or a person's session: never stored, never shown (L3). */
const SECRET = /^(token|access_?token|id_?token|auth|authorization|password|passwd|secret|sig|signature|session|sid|api_?key|apikey)$/i;

/** L1: the one form of a URL we compare and store. Null when it is not an http(s) address at all (L2, scheme and credentials only). */
export function normalizeLinkUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s || s.length > 2000 || /[\s\u0000-\u001f]/.test(s)) return null;
  let u: URL;
  try { u = new URL(s); } catch { return null; }
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  if (u.username || u.password) return null;
  u.hash = "";
  for (const k of [...u.searchParams.keys()]) if (TRACKING.test(k) || SECRET.test(k)) u.searchParams.delete(k);
  u.searchParams.sort();
  let host = u.hostname.toLowerCase();
  if (host.startsWith("www.")) host = host.slice(4);
  const port = u.port && !((u.protocol === "https:" && u.port === "443") || (u.protocol === "http:" && u.port === "80")) ? `:${u.port}` : "";
  const path = u.pathname.length > 1 ? u.pathname.replace(/\/+$/, "") : "";
  const q = u.searchParams.toString();
  // http and https of the same address are one link: store the https form (a site that has moved to https is not a different page).
  return `${u.protocol === "http:" && !port ? "https:" : u.protocol}//${host}${port}${path || ""}${q ? `?${q}` : ""}`;
}

/** The URL as the person's browser would open it (for display and for the re-check): scheme kept, fragment dropped. */
export function displayLinkUrl(raw: string): string | null {
  const n = normalizeLinkUrl(raw);
  if (!n) return null;
  try {
    const u = new URL(raw.trim());
    u.hash = "";
    for (const k of [...u.searchParams.keys()]) if (SECRET.test(k)) u.searchParams.delete(k);
    return u.toString();
  } catch { return null; }
}

/** True when `href` is an absolute http(s) address on a host other than `ownHost`: the only links that get the control. */
export function isExternalHref(href: string | null | undefined, ownHost: string): boolean {
  if (!href) return false;
  let u: URL;
  try { u = new URL(href); } catch { return false; }
  if (u.protocol !== "http:" && u.protocol !== "https:") return false;
  const strip = (h: string) => h.toLowerCase().replace(/^www\./, "");
  return strip(u.hostname) !== strip(ownHost.replace(/:\d+$/, ""));
}

/* ─────────────── private addresses (L2) ─────────────── */

/** An IPv4 address in dotted form, as `new URL(...).hostname` gives it (hex, octal and decimal forms are already dotted). */
const V4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

export function isPrivateIPv4(ip: string): boolean {
  const m = V4.exec(ip);
  if (!m) return false;
  const [a, b] = [Number(m[1]), Number(m[2])];
  if ([1, 2, 3, 4].some((i) => Number(m[i]) > 255)) return true; // not an address: refuse
  return a === 0 || a === 10 || a === 127 || a >= 224
    || (a === 100 && b >= 64 && b <= 127) // carrier-grade NAT
    || (a === 169 && b === 254) // link-local, and the cloud metadata address 169.254.169.254
    || (a === 172 && b >= 16 && b <= 31)
    || (a === 192 && b === 168)
    || (a === 192 && b === 0 && Number(m[3]) === 0) // IETF protocol assignments
    || (a === 198 && (b === 18 || b === 19)); // benchmarking
}

/** IPv6 (brackets removed): loopback, unspecified, unique-local, link-local, multicast, and v4-mapped/-compatible forms of a private v4. */
export function isPrivateIPv6(ip: string): boolean {
  const s = ip.toLowerCase().replace(/^\[|\]$/g, "").replace(/%.*$/, "");
  if (!s.includes(":")) return false;
  if (s === "::" || s === "::1") return true;
  const mapped = /^(?:0{0,4}:){1,5}(?:ffff:)?(\d{1,3}(?:\.\d{1,3}){3})$/.exec(s) ?? /^::ffff:(\d{1,3}(?:\.\d{1,3}){3})$/.exec(s);
  if (mapped) return isPrivateIPv4(mapped[1]!);
  // ::ffff:7f00:1 (hex form of a mapped v4)
  const hex = /^(?:0{0,4}:){0,5}ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/.exec(s);
  if (hex) { const hi = parseInt(hex[1]!, 16), lo = parseInt(hex[2]!, 16); return isPrivateIPv4(`${hi >> 8}.${hi & 255}.${lo >> 8}.${lo & 255}`); }
  const first = parseInt(s.split(":")[0] || "0", 16);
  return (first & 0xfe00) === 0xfc00 // fc00::/7
    || (first & 0xffc0) === 0xfe80 // fe80::/10
    || (first & 0xff00) === 0xff00 // multicast
    || first === 0; // ::/8 (includes ::x forms we did not list)
}

export const isPrivateAddress = (ip: string): boolean => (ip.includes(":") ? isPrivateIPv6(ip) : isPrivateIPv4(ip));

/**
 * L2, the part that needs no network: why this URL must not be fetched, or null when its text is fine. The server adds the
 * DNS check on top (a public name can point at a private number).
 */
export function blockedLinkReason(raw: string): string | null {
  const n = normalizeLinkUrl(raw);
  if (!n) return "Only web addresses (http or https) can be checked.";
  let u: URL;
  try { u = new URL(raw.trim()); } catch { return "That isn't a web address."; }
  const host = u.hostname.toLowerCase().replace(/^\[|\]$/g, "").replace(/\.$/, "");
  if (!host) return "That isn't a web address.";
  if (host.includes(":")) return isPrivateIPv6(host) ? "That address is inside a private network." : null;
  if (V4.test(host)) return isPrivateIPv4(host) ? "That address is inside a private network." : null;
  if (host === "localhost" || host.endsWith(".localhost") || host.endsWith(".local") || host.endsWith(".internal") || host.endsWith(".lan") || host.endsWith(".home.arpa") || host.endsWith(".corp")) return "That address is inside a private network.";
  if (!host.includes(".")) return "That address is inside a private network."; // a bare name resolves only on a private network
  if (/^[0-9.]+$/.test(host) || /^0x[0-9a-f]+$/i.test(host)) return "That address is inside a private network."; // odd numeric forms
  return null;
}

/* ─────────────── what a re-check means (L5) ─────────────── */

/** The same page at another address? Scheme, "www.", case, a trailing slash and the fragment don't count as moved. */
export function sameLinkDestination(a: string, b: string): boolean {
  const x = normalizeLinkUrl(a), y = normalizeLinkUrl(b);
  return !!x && !!y && x === y;
}

export interface RawProbe {
  /** HTTP status of the last response, when there was one. */
  status?: number;
  /** The address the last response came from. */
  finalUrl?: string;
  /** No response: "no such host", "timed out", "refused", "too many redirects", "blocked". */
  error?: string;
}

/** L5. Pure: the probe is the only input, so the same words come out in tests and on the server. */
export function verdictOf(url: string, p: RawProbe, at: IsoDateTime): LinkCheck {
  const base = { checkedAt: at, ...(p.status !== undefined ? { status: p.status } : {}), ...(p.finalUrl ? { finalUrl: p.finalUrl } : {}) };
  if (p.status === undefined || p.error) return { ...base, verdict: "broken", error: p.error ?? "no answer" };
  if (p.status >= 400) {
    const refuses = [401, 403, 429, 999].includes(p.status);
    return { ...base, verdict: "broken", ...(refuses ? { note: "Some sites refuse automated checks. Open it yourself before you change it." } : {}) };
  }
  if (p.status >= 200 && p.status < 400) {
    if (p.finalUrl && !sameLinkDestination(url, p.finalUrl)) {
      let lostPath = false;
      try { lostPath = new URL(url).pathname.replace(/\/+$/, "").length > 0 && new URL(p.finalUrl).pathname.replace(/\/+$/, "").length === 0; } catch { /* keep false */ }
      return { ...base, verdict: "redirected", suggestedUrl: p.finalUrl, ...(lostPath ? { note: "It lands on the site's home page: the page we link to may be gone." } : {}) };
    }
    return { ...base, verdict: "works", note: "Works for us. It may be a regional or temporary problem." };
  }
  return { ...base, verdict: "broken", error: `status ${p.status}` };
}

/* ─────────────── who hears, and when (L4) ─────────────── */

/** True when the team is told now, not in the daily digest. */
export function notifyNow(i: { kind: ReportLinkKind; count: number }): boolean {
  // Official data goes in the daily digest only (owner, 2026-10-04): it is a correction, never a safety link.
  if (i.kind === "official-data") return false;
  return isSafetyCriticalKind(i.kind) || i.count >= LINK_URGENT_COUNT;
}

/** Fixed words for the team (no URL in a lock-screen title; the URL is in the queue). */
export const LINK_TEAM_TITLE = "A link needs a fix.";
export const LINK_TEAM_URGENT_TITLE = "A safety link may be dead.";
export const LINK_THANKS = "Thanks, we'll fix it.";
/** The note is one line. */
export const NOTE_LIMIT = 140;
export const LINK_FIXED_TITLE = "The link you reported is fixed.";

/** Where a data key lives: the part before the first "/" names the source (e.g. "nv"); the rest is the entry. */
export const linkKeySource = (key: string): string => key.split("/")[0]!;
